import { useEffect, useState } from "react";
import { useHealth } from "../hooks/useHealth";
import { useAuth } from "../hooks/useAuth";
import { useCurrentSystem } from "../hooks/useCurrentSystem";
import { useStaticSystemsById } from "../hooks/useStaticSystems";
import { CLASS_LABEL } from "../lib/systemClass";
import { AddCharactersModal } from "./AddCharactersModal";
import { ChainSwitcher } from "./ChainSwitcher";
import { DayTripButton } from "./DayTripButton";
import { PilotMenu } from "./PilotMenu";
import { ShortcutsPopover } from "./ShortcutsPopover";
import { DisplayOptionsPopover } from "./DisplayOptionsPopover";
import { VersionUpdateBanner } from "./VersionUpdateBanner";

export function Header() {
  const health = useHealth();
  const auth = useAuth();
  const [addCharactersOpen, setAddCharactersOpen] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    const queryIndex = hash.indexOf("?");
    if (queryIndex === -1) return;
    const params = new URLSearchParams(hash.slice(queryIndex + 1));
    if (params.get("add-character") !== "ok") return;
    setAddCharactersOpen(true);
    params.delete("add-character");
    const path = hash.slice(0, queryIndex);
    const rest = params.toString();
    const cleaned = rest ? `${path}?${rest}` : path;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}${cleaned}`,
    );
  }, []);

  const serverUp = health.data?.status === "ok";

  return (
    <>
      <VersionUpdateBanner />
      <header className="topbar">
        <div className="topbar__left">
          <h1>
            <span className="topbar__star" aria-hidden="true">✧</span>
            k162
          </h1>
          {auth.data && (
            <>
              <div className="topbar__pilot-system">
                <PilotMenu
                  characters={auth.data.characters}
                  activeCharacterId={auth.data.active_character_id}
                  activeName={auth.data.name}
                  isServerOwner={auth.data.is_server_owner}
                  onOpenAddCharacters={() => setAddCharactersOpen(true)}
                />
                <CurrentSystemInline />
              </div>
              <ChainSwitcher />
            </>
          )}
        </div>
        <div className="topbar__right">
          {auth.data && <DayTripButton />}
          <ShortcutsPopover />
          <DisplayOptionsPopover />
          <span className="status">
            {health.isLoading
              ? "connecting…"
              : serverUp
                ? (health.data?.version ?? "connected")
                : "connection error"}
            <span
              className={`status__dot status__dot--${
                health.isLoading ? "pending" : serverUp ? "up" : "down"
              }`}
              aria-hidden="true"
            />
          </span>
        </div>
      </header>
      {auth.data && (
        <AddCharactersModal
          open={addCharactersOpen}
          onOpenChange={setAddCharactersOpen}
        />
      )}
    </>
  );
}

function CurrentSystemInline() {
  const { data } = useCurrentSystem();
  const staticSystems = useStaticSystemsById();
  if (!data) return null;
  const sys = staticSystems.byId(data.id);
  const cls = sys?.class;
  const security = sys?.security;
  const showSec = (cls === "HS" || cls === "LS") && security != null;
  const secTier = showSec
    ? Math.min(10, Math.max(1, Math.round(security * 10)))
    : null;
  return (
    <>
      <span className="topbar__current-system__sep" aria-hidden="true">@</span>
      <div className="topbar__current-system">
        {cls && (
          <span
            className={`system-node__class system-class--${cls}`}
            title={cls}
          >
            {CLASS_LABEL[cls]}
          </span>
        )}
        <span className="topbar__current-system__name">{data.name}</span>
        {showSec && (
          <span
            className={`topbar__current-system__sec security-tier-${secTier}`}
          >
            ({security.toFixed(1)})
          </span>
        )}
      </div>
    </>
  );
}
